import { useMemo } from "react";
import flatten from "lodash/flatten";
import { useInfiniteQuery } from "react-query";
import type { UseInfiniteQueryOptions } from "react-query";
import { reactQueryGet } from "@/react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import type { Paywall } from "../types/Paywall";

export const DEFAULT_PAGE_SIZE = 30;

export const QUERY_KEY = "paywalls";

interface UsePaywallsInfiniteQueryProps {
  filters?: Record<string, string | string[]>;
  initialPage?: number;
  options?: Partial<UseInfiniteQueryOptions<PaywallSearchResponse>>;
  perPage?: number;
  query?: string;
  sort?: Record<string, string>;
}

interface PaywallSearchParams {
  filters?: Record<string, string | string[]>;
  order?: Record<string, string>;
  page?: number;
  per_page?: number;
  query?: string;
}

interface PaywallSearchResponse {
  count: number;
  has_next_page: boolean;
  meta: {
    total_count: number;
  };
  page: number;
  page_count: number;
  per_page: number;
  records: Paywall[];
}

export const usePaywallsInfiniteQuery = (
  args: UsePaywallsInfiniteQueryProps = {},
) => {
  const {
    query = "",
    filters = {},
    perPage = DEFAULT_PAGE_SIZE,
    initialPage = 1,
    options = {},
    sort = {},
  } = args;

  const fetchPage = ({ pageParam = initialPage }) => {
    let page = 1;

    if (typeof pageParam === "number" && pageParam > 0) {
      page = pageParam;
    }

    const formatFilters = (filters: Record<string, string | string[]>) => {
      const formattedFilters: Record<string, string | string[]> = {};
      for (const [key, value] of Object.entries(filters)) {
        formattedFilters[key] = value;
      }
      return formattedFilters;
    };

    const params: PaywallSearchParams = {
      page,
      filters: formatFilters(filters),
      per_page: perPage,
    };

    if (query) {
      params.query = query;
    }

    if (Object.keys(sort).length > 0) {
      params.order = sort;
    }

    return reactQueryGet(internalApi.search.paywalls.index({ params }));
  };

  const queryKey = [QUERY_KEY, query, filters, perPage, JSON.stringify(sort)];

  const queryOptions: Partial<UseInfiniteQueryOptions<PaywallSearchResponse>> =
    {
      getNextPageParam: (lastPage: PaywallSearchResponse) =>
        lastPage.has_next_page ? lastPage.page + 1 : undefined,
      getPreviousPageParam: (firstPage: PaywallSearchResponse) =>
        firstPage.page > 1 ? firstPage.page - 1 : undefined,
      keepPreviousData: true,
      ...options,
    };

  const result = useInfiniteQuery<PaywallSearchResponse>(
    queryKey,
    fetchPage,
    queryOptions,
  );
  const pages = useMemo(() => result.data?.pages || [], [result.data?.pages]);
  const totalPaywalls = pages[0]?.meta?.total_count ?? 0;
  const totalQueryPaywalls = pages[0]?.count ?? 0;
  const pageSize = pages[0]?.per_page ?? perPage;
  const paywalls = useMemo(
    () => flatten<Paywall>(pages.map(page => page.records)),
    [pages],
  );

  return {
    ...result,
    queryKey,
    totalPaywalls,
    totalQueryPaywalls,
    paywalls,
    pageSize,
    pages,
  };
};
